// src/services/watchlistService.js
import { API_BASE_URL } from '../utils/constants';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
});

const handleResponse = async (response) => {
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/';
    }
    // 204 No Content has no body
    if (response.status === 204) return null;
    const error = await response.json();
    throw new Error(error.error || error.detail || 'Request failed');
  }
  if (response.status === 204) return null;
  return response.json();
};

export const watchlistService = {
  /** GET /api/stocks/watchlist/ — full list with stock data */
  async getWatchlist() {
    try {
      const response = await fetch(`${API_BASE_URL}/stocks/watchlist/`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching watchlist:', error);
      throw error;
    }
  },

  /** POST /api/stocks/watchlist/add/ — add stock by symbol */
  async addToWatchlist(symbol) {
    try {
      const response = await fetch(`${API_BASE_URL}/stocks/watchlist/add/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ symbol }),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error adding to watchlist:', error);
      throw error;
    }
  },

  /** DELETE /api/stocks/watchlist/remove/<symbol>/ — remove stock */
  async removeFromWatchlist(symbol) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/stocks/watchlist/remove/${symbol}/`,
        {
          method: 'DELETE',
          headers: getAuthHeaders(),
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error removing from watchlist:', error);
      throw error;
    }
  },

  /** GET /api/stocks/watchlist/check/<symbol>/ — is this stock watched? */
  async checkWatched(symbol) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/stocks/watchlist/check/${symbol}/`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include',
        }
      );
      const data = await handleResponse(response);
      return data?.watched ?? false;
    } catch (error) {
      console.error('Error checking watchlist status:', error);
      return false;
    }
  },

  /** GET /api/stocks/watchlist/stats/<symbol>/?interval=<interval>
   *  Returns OHLC + median stats for the given interval window.
   *  Response shape:
   *  {
   *    symbol, interval, display_currency,
   *    open, open_display,
   *    close, close_display,
   *    high, high_display,
   *    low, low_display,
   *    median, median_display,
   *    data_points
   *  }
   */
  async getOhlcStats(symbol, interval = '1hr') {
    try {
      const response = await fetch(
        `${API_BASE_URL}/stocks/watchlist/stats/${symbol}/?interval=${interval}`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching OHLC stats:', error);
      return null;
    }
  },

  /** GET /api/stocks/watchlist/price-alerts/?is_read=<true|false>
   *  Returns WatchlistPriceAlert records for the logged-in user.
   *  Pass { is_read: false } to get unread-only (used by AlertsWidget badge).
   */
  async getPriceAlerts(params = {}) {
    try {
      const query = params.is_read !== undefined
        ? `?is_read=${params.is_read}`
        : '';
      const response = await fetch(
        `${API_BASE_URL}/stocks/watchlist/price-alerts/${query}`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching price alerts:', error);
      return [];
    }
  },

  /** PATCH /api/stocks/watchlist/price-alerts/
   *  Mark specific alert ids as read, or mark all as read.
   *  Usage:
   *    markPriceAlertsRead({ ids: [1, 2] })
   *    markPriceAlertsRead({ mark_all: true })
   */
  async markPriceAlertsRead(payload) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/stocks/watchlist/price-alerts/`,
        {
          method: 'PATCH',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify(payload),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error marking price alerts as read:', error);
      throw error;
    }
  },
};